import type {
  EnrichedAction,
  PreparedAction,
  ActionClickStatus,
  ResolutionStatus,
} from '../../services/recommendations_contracts'
import type { StatusBadgeColor } from '../FbUI/StatusBadge'
import { SIGNAL_CONFIG } from './recommendationConfig'

/**
 * Human label for a prepared action, in present ("Update deal stage") or past
 * ("Updated deal stage") tense. ``add_note`` resolves company- vs deal-note copy
 * from the signal's link target. Pure — shared by the card body's applied
 * checklist and the completed-panel step list so both read identically.
 *
 * Extracted from EnrichedActionCard (which pulls in the Quill editor) so this
 * can be imported by node-side code + unit tests without dragging the DOM in.
 */
export function preparedActionLabel(
  pa: PreparedAction,
  signalType: EnrichedAction['signal_type'],
  dealId: number | null,
  past = false
): string {
  if (pa.type === 'update_stage')
    return past ? 'Updated deal stage' : 'Update deal stage'
  if (pa.type === 'update_close_date')
    return past ? 'Updated close date' : 'Update close date'
  if (pa.type === 'create_reminder')
    // "Set" is its own past tense, but branch explicitly for consistency so a
    // future copy change only has to touch this one arm.
    return past ? 'Set follow-up reminder' : 'Set follow-up reminder'
  if (pa.type === 'reschedule_reminder')
    return past ? 'Rescheduled reminder' : 'Reschedule reminder'
  if (pa.type === 'delete_reminder')
    return past ? 'Deleted reminder' : 'Delete reminder'
  if (pa.type === 'add_note') {
    const linkTarget = SIGNAL_CONFIG[signalType]?.linkTarget ?? 'auto'
    const targetIsCompany =
      linkTarget === 'company' || (linkTarget === 'auto' && !dealId)
    const target = targetIsCompany ? 'company note' : 'deal note'
    return `${past ? 'Added' : 'Add'} ${target}`
  }
  if (pa.type === 'send_email') return past ? 'Sent email' : 'Send email'
  if (pa.type === 'create_contact')
    return past ? 'Created contact' : 'Create contact'
  if (pa.type === 'update_contact')
    return past ? 'Updated contact' : 'Update contact'
  if (pa.type === 'create_deal') return past ? 'Created deal' : 'Create deal'
  return (pa as PreparedAction).type
}

/**
 * Maps a resolved action to a status badge: applied / auto-resolved read as
 * `success` (green), dismissals as `neutral` (gray). Prefers the enriched
 * action's ``resolution_status`` (set by the LLM or the user's action), falling
 * back to the click-based status for data persisted before that field existed.
 */
export function getResolvedBadge(
  status: ActionClickStatus | null,
  autoCompleted: boolean,
  resolutionStatus?: ResolutionStatus | null
): { label: string; color: StatusBadgeColor } | null {
  // Prefer enriched_action-level resolution_status (set by LLM or user action)
  if (resolutionStatus) {
    switch (resolutionStatus) {
      case 'auto_resolved':
        return { label: 'Auto-resolved', color: 'success' }
      case 'auto_dismissed':
        return { label: 'Auto-dismissed', color: 'neutral' }
      case 'user_resolved':
        return { label: 'Applied', color: 'success' }
      case 'user_dismissed':
        return { label: 'Dismissed', color: 'neutral' }
    }
  }
  // Fallback: existing click-based logic for backward compat with old data
  if (status === 'done') {
    return {
      label: autoCompleted ? 'Auto-resolved' : 'Applied',
      color: 'success',
    }
  }
  if (status === 'dismissed') {
    return {
      label: autoCompleted ? 'Auto-resolved' : 'Dismissed',
      color: autoCompleted ? 'success' : 'neutral',
    }
  }
  return null
}
